
import React from 'react';
import { Link } from 'react-router-dom';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { CartItem as CartItemType, useCartStore } from '@/store/useCartStore';
import { formatCurrency, calculateDiscountedPrice } from '@/lib/utils';
import TranslatedText from '@/TranslatedText';

interface CartItemProps {
  item: CartItemType;
}

const CartItem: React.FC<CartItemProps> = ({ item }) => {
  const { removeItem, updateQuantity } = useCartStore();
  
  const handleIncrement = () => {
    updateQuantity(item.id, item.quantity + 1);
  };
  
  const handleDecrement = () => {
    if (item.quantity > 1) {
      updateQuantity(item.id, item.quantity - 1);
    } else {
      removeItem(item.id);
    }
  };

  const originalPrice = parseFloat(item.price);
  const discountedPrice = item.discount 
    ? calculateDiscountedPrice(originalPrice, item.discount) 
    : originalPrice;
  
  return (
    <div className="flex gap-3 py-3 border-b border-gray-100 last:border-none">
      <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden border border-gray-100">
        <AspectRatio ratio={1 / 1}>
          <img 
            src={item.image} 
            alt={item.name} 
            className="w-full h-full object-cover"
          />
        </AspectRatio>
      </div>
      
      <div className="flex-1 min-w-0">
        <Link 
          to={`/product/${item.id}`}
          className="text-sm font-medium line-clamp-1 hover:text-ahavex-blue transition-colors"
        >
          {item.name}
        </Link>
        
        <div className="flex items-center gap-2 mt-1">
          <span className="text-sm font-medium">
            {formatCurrency(discountedPrice, item.currency)}
          </span>
          
          {item.discount && (
            <span className="text-xs line-through text-gray-400">
              {formatCurrency(originalPrice, item.currency)}
            </span>
          )}
          
          {item.discount && (
            <span className="text-xs font-medium text-red-600">
              -{item.discount}%
            </span>
          )}
        </div>
        
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-7 w-7 p-0 rounded-l-md rounded-r-none border-r-0"
              onClick={handleDecrement}
              aria-label="Decrease quantity"
            >
              {item.quantity === 1 ? <Trash2 size={14} /> : <Minus size={14} />}
            </Button>
            
            <div className="h-7 min-w-[2rem] flex items-center justify-center text-sm border-y border-input">
              {item.quantity}
            </div>
            
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-7 w-7 p-0 rounded-r-md rounded-l-none border-l-0"
              onClick={handleIncrement}
              aria-label="Increase quantity"
            >
              <Plus size={14} />
            </Button>
          </div>
          
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-gray-500 hover:text-red-600"
            onClick={() => removeItem(item.id)}
            aria-label="Remove item"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
